// Navbar.tsx
import { useState } from 'react';
import { useAuth } from './AuthContext';
import MiniLogin from './MiniLogin';
import styles from './Navbar.module.css';
import AdminImpersonationPanel from './admin/AdminImpersonationPanel';
import menuIcon from './img/menu.svg';
import wankandoo from './img/wankandoo.png';
import SafeNavLink from './components/SafeNavLink';
import { STUDENT_HOME, TEACHER_HOME } from './utils/roleHome';

interface NavItem {
  to: string;
  label: string;
  /** NavLink `end`: nur exakte Übereinstimmung gilt als aktiv. */
  end?: boolean;
}

/**
 * Menüpunkte pro Rolle (UX-Plan Phase 1.5). Reihenfolge = Wichtigkeit:
 * Die Kursliste steht zuerst, weil dort jede Aufgabe beginnt. „Kontakt" liegt im
 * Footer und im Profil-Menü, nicht mehr im Hauptmenü.
 */
export function linksForRole(role: string | null, isLoggedIn: boolean): NavItem[] {
  if (!isLoggedIn) {
    return [
      { to: '/', label: 'Startseite', end: true },
      { to: '/hilfe', label: 'Hilfe' },
      { to: '/register', label: 'Registrieren' },
      { to: '/login', label: 'Login' },
    ];
  }
  if (role === 'teacher') {
    return [
      // Bewusst kein Menüpunkt „Stundenplanung“: die Planung startet aus der Kurskarte
      // („Zur Planung“), ein zweiter Einstieg über eine Kursauswahl war Doppelung.
      { to: TEACHER_HOME, label: 'Deine Kurse' },
      { to: '/hilfe', label: 'Hilfe' },
    ];
  }
  if (role === 'student') {
    return [
      { to: '/student', label: 'Deine Zeiten', end: true },
      { to: STUDENT_HOME, label: 'Deine Kurse' },
      { to: '/hilfe', label: 'Hilfe' },
    ];
  }
  // Admin ohne Impersonation
  return [{ to: '/hilfe', label: 'Hilfe' }];
}

interface NavbarProps {
  /** Mobil mit Tab-Leiste (RootLayout): Burger-Menü und Linkliste entfallen, die Marke bleibt. */
  tabBarMode?: boolean;
}

const Navbar = ({ tabBarMode = false }: NavbarProps) => {
  const { isLoggedIn, userRole, isImpersonating } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Wenn Impersonation aktiv ist: zeige sowohl Admin- als auch impersonierte User-Navlinks
  const showAdminLinks = isImpersonating || userRole === 'admin';
  const effectiveUserRole = userRole; // Die Rolle des impersonierten Users (oder aktuellen Users)

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const links = linksForRole(effectiveUserRole, isLoggedIn);

  return (
    <>
      <nav className={`${styles.navbar} ${effectiveUserRole === 'teacher' ? styles.teacher : ''} ${effectiveUserRole === 'student' ? styles.student : ''} ${showAdminLinks ? styles.admin : ''} ${isImpersonating ? styles.impersonating : ''} ${tabBarMode ? styles.tabBarMode : ''}`}>
        <div className={styles.navbarContent}>
          <div className={styles.navbarBrand}>
            <img src={wankandoo} alt="Wankandoo Logo" className={styles.logo} />
            <span className={styles.brandName}>{("WAN\n  KAN\n    DOO")}</span>
            {tabBarMode ? (
              // Mobil mit Tab-Leiste: kein Burger (Kurse/Hilfe/Profil stehen unten),
              // aber das Profil-Menü oben rechts – sonst fand niemand „Abmelden“.
              // Bei Impersonation führt nur dieser Weg zum Admin-Panel.
              <div className={styles.tabBarProfile} data-testid="navbar-profile-menu">
                <MiniLogin
                  extraItems={showAdminLinks ? [{ label: 'Admin', onClick: () => setShowAdminPanel(v => !v) }] : []}
                />
              </div>
            ) : (
              <button
                className={styles.menuButton}
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label="Menü"
              >
                <img src={menuIcon} alt="" className={styles.menuIcon} />
              </button>
            )}
          </div>

          {/* Impersonation-Banner für bessere Sichtbarkeit */}
          {isImpersonating && (
            <div className={styles.impersonationBanner}>
              <span className={styles.impersonationBadgeText}>
                🔄 Impersoniert als {effectiveUserRole}
              </span>
            </div>
          )}

          <div className={`${styles.navbarLinks} ${isMenuOpen ? styles.active : ''}`} >
            {links.map(item => (
              <SafeNavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => isActive ? styles.active : undefined}
                toggleMenu={toggleMenu}
              >
                {item.label}
              </SafeNavLink>
            ))}

            {showAdminLinks && (
              <>
                <SafeNavLink to="/multi-view" className={({ isActive }) => isActive ? styles.active : undefined} toggleMenu={toggleMenu}>
                  Multi-View
                </SafeNavLink>
                <button
                  className={styles.adminButton}
                  onClick={() => setShowAdminPanel((v) => !v)}
                  aria-expanded={showAdminPanel}
                >
                  Admin
                </button>
              </>
            )}
          </div>

          {!tabBarMode && (
            <div className={`${styles.miniLoginContainer} ${isMenuOpen ? styles.active : ''}`}>
              <MiniLogin />
            </div>
          )}
        </div>
      </nav>

      {showAdminPanel && (
        <AdminImpersonationPanel onClose={() => setShowAdminPanel(false)} />
      )}
    </>
  );
};

export default Navbar;
